// PackageDetails.jsx
import { useState, useEffect, useRef } from "react";
import { Box, CircularProgress, Divider } from "@mui/material";
import { SectionHeader } from "@/Components/Header/SectionsHeader";
import { LuX } from "@/Components/Icons/Icons";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import { useUser } from "@/Context/UserContext";
import TableGenerator from "@/Utils/FormGenerator/TableGenerator";
import InputTextField from "@/Components/Form/InputTextField";
import dayjs from "dayjs";
import DateRangeSelector from "@/Components/Form/DateRangeCalendar";
import {
  useGetImportBondTypeQuery,
  useLazyGetImportBondTypeQuery,
} from "@/redux/ExportPage/CreateNewJob/Shipment/ShipmentMainSlice";
import { hookMap } from "@/redux/hookMap";
import InputDropdown from "@/Components/Form/InputDropdown";
import SuccessDialog from "@/Components/Dialog/SuccessDialog";
import FormDialog from "@/Components/Dialog/FormDialog";
import ProductDeclarationSchema from "@/Config/Export/CreateNewJob/ProductDeclarationSchema";
import { descriptions } from "@/Config/Export/CreateNewJob/ProductDeclarationSchema";

import { useParams } from "react-router";
import { BiSolidError } from "react-icons/bi";
import { LuCheck, LuInfo } from "react-icons/lu";
import {
  useLazyGetImportProductDeclarationQuery,
  useCreateImportProductDeclarationMutation,
  useUpdateImportProductDeclarationMutation,
  useDeleteImportProductDeclarationMutation,
} from "@/redux/ImportPage/CreateNewJob/Product/Declaration/importProductDeclaration";

export default function ImportProductDeclaration({ onClose }) {
  const { setFormData, setValueGlobal, formData } = useUser();
  const [importJob, setImportJob] = useState(null);
  const [invoiceNo, setInvoiceNo] = useState(null);
  const [productNo, setProductNo] = useState(null);
  const [formDialogData, setFormDialogData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [trigger, setTrigger] = useState(0);
  const formRef = useRef();
  const { importerId } = useParams();
  const [
    fetchDeclaration,
    { data: declarationData, isLoading: declarationLoading, isFetching, error },
  ] = useLazyGetImportProductDeclarationQuery();
  let selectedInvoice, selectedProduct;

  const [createDeclaration] = useCreateImportProductDeclarationMutation();
  const [updateDeclaration] = useUpdateImportProductDeclarationMutation();
  const [deleteDeclaration] = useDeleteImportProductDeclarationMutation();
  const handleSave = async () => {
    try {
      const payload = await formRef.current.submit();
      const finalPayload = {
        ...payload,
        declarationTitle : payload.declarationCode
      }
      let response;
      if (isEditing) {
        response = await updateDeclaration({
          id: invoiceId,
          subId: formDialogData.id,
          data: finalPayload,
        });
      } else {
        const finalPayload = {
          ...payload,
          declarationInvoice: invoiceId,
          declarationTitle : payload.declarationCode
        };
        response = await createDeclaration({
          id: invoiceId,
          data: finalPayload,
        });
      }
      // Extract success info safely
      const success =
        response?.data?.statusCode === 200 || response?.data?.isSuccess ;
      const message =
        response?.data?.message ||
        response?.message ||
        "Data saved successfully";

      // Show success dialog
      setShowForm(false);
      setShowSuccessDialog(true);

      setSuccessDialogConfig({
        label: success ? "Success" : "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {message}
          </strong>
        ),
        buttonText: "OK",
        IconComponent: success ? LuCheck : BiSolidError,
        status: success ? "success" : "error",
      });
    } catch (error) {
      console.error("Declaration Save Error:", error);

      const message =
        error?.data?.message ||
        error?.error ||
        "Something went wrong while saving.";

      // Show error dialog
      setShowSuccessDialog(true);

      setSuccessDialogConfig({
        label: "Error",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {message}
          </strong>
        ),
        buttonText: "OK",
        IconComponent: BiSolidError,
        status: "error",
      });
    } finally {
      setTrigger(trigger + 1);
      setFormDialogData({});
      setIsEditing(false);

    }
  };

  const handleActions = () => {};

  const handleCloseForm = () => {
    setIsEditing(false);
    setFormDialogData({});
    setShowForm(false);
  };
  useEffect(() => {
    selectedInvoice = formData?.invoiceMain?.find((item) => item.selected);
    selectedProduct = formData?.productMain?.find((item) => item.selected);
  
    setInvoiceId(selectedInvoice?.id);
    if (selectedInvoice && selectedProduct) {
      setInvoiceNo(
        `[#00${selectedInvoice?.srNo}] ${selectedInvoice?.importInvoiceNumber}`
      );

      setProductNo(
        `[#00${selectedProduct?.SrNo}] ${selectedProduct?.importProductMainDescriptionId?.name || selectedProduct.importProductMainDescription}`
      );
    }
  }, [formData, trigger]);

  useEffect(() => {
    const params = {};
    if (!invoiceId) return;
    const fetchDeclarationData = async () => {
      const response = await fetchDeclaration({ id: invoiceId });
      setFormData((prev) => ({
        ...prev,
        importProductDeclaration: response?.data ?? [],
      }));
    };
    fetchDeclarationData();
  }, [invoiceNo, trigger]);

  const visibleFields = [
    { key: "declarationType", label: "Type", width: 120 },
    { key: "declarationCode", label: "Title", width: 180 },
    {
      key: "declarationRemarks",
      label: "",
      width: 50,
      icon: <LuInfo size={18} />, // 👈 icon here
      showAsIcon: true,
    },
  ];

  const handlers = {
    handleRowClick: (row) => {
      if (!row) return;
      setImportJob(row);
    },
    handleEdit: (row) => {
      setFormDialogData(row);
      setIsEditing(true);
      setShowForm(true);
    },
    handleRemove: async (row) => {
      try {
        if (!row?.id) return;

        const response = await deleteDeclaration({ id: row.id });

        // Check backend success
        const success =
          response?.data?.statusCode === 200 || response?.status === "success";

        setSuccessDialogConfig({
          label: success ? "Success" : "Failure",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {response?.data?.message ||
                response?.message ||
                (success ? "Deleted successfully" : "Failed to delete")}
            </strong>
          ),
          buttonText: "OK",
          IconComponent: success ? LuCheck : BiSolidError,
          status: success ? "success" : "error",
        });

        setShowSuccessDialog(true);

        if (success) {
          // Refresh table
          setTrigger((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Delete error:", error);

        // Show error dialog
        setSuccessDialogConfig({
          label: "Error",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {error?.data?.message || "Something went wrong while deleting."}
            </strong>
          ),
          buttonText: "OK",
          IconComponent: BiSolidError,
          status: "error",
        });

        setShowSuccessDialog(true);
      }
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--gray-0)",
        width: "100%",
        maxWidth: "100vw",
        height: 700,
        // maxHeight: 600,
        mb: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: 2,
          m: 0.5,
        }}
      >
        <SectionHeader title={"Declarations"} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            gap: 4,
            alignItems: "center",
          }}
        >
          {/* 👉 New Button */}
          <OutLinedButton
            label="Add Declaration"
            onClick={() => setShowForm(true)}
            sx={{ padding: "4px 12px" }}
          />

          {/* Existing close icon */}
          <LuX size={20} style={{ cursor: "pointer" }} onClick={onClose} />
        </Box>
      </Box>
      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <Box
          sx={{
            border: "1px solid var(--gray-400)",
            borderRadius: 2,
            p: 1,
            m: 1,
            backgroundColor: "var(--gray-50)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {/* 🔹 Row 1 — Job No, BE No, Customer */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <InputTextField
              name="invoiceNo"
              label="Invoice No"
              value={invoiceNo}
              disabled={true}
            />

            <InputTextField
              name="product"
              label="Product"
              value={productNo}
              disabled={true}
            />
          </Box>
        </Box>

        {/* Loader + Table */}
        {isFetching && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              width: "100%",
              gap: 1,
            }}
          >
            <CircularProgress size={40} sx={{ color: "var(--brand-600)" }} />
          </Box>
        )}

        {!isFetching && (
          <TableGenerator
            compKey={"importProductDeclaration"}
            visibleFields={visibleFields}
            label={"Declaration details"}
            handlers={handlers}
          />
        )}

        <SuccessDialog
          open={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          {...successDialogConfig}
        />
        {showForm && (
          <FormDialog
            title={isEditing ? `Edit Declaration` : `Add Declaration`}
            schema={ProductDeclarationSchema}
            initialData={formDialogData}
            isEditing={isEditing}
            onSubmit={() => handleSave("table")}
            handleActions={handleActions}
            onClose={handleCloseForm}
            showCancelButton={true}
            showSubmitButton={true}
            cancelLabel="Cancel"
            submitLabel={isEditing ? "Save" : "Add"}
            loading={loading}
            ref={formRef}
            // disabled={!isRequiredFieldFilled}
          />
        )}
      </Box>
    </Box>
  );
}
